import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Server, Globe, Terminal } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ErrorBoundary } from "@/components/shared/ErrorBoundary";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { TokenList } from "@/components/tokens/TokenList";
import { CreateTokenDialog } from "@/components/tokens/CreateTokenDialog";
import { RevokeTokenDialog } from "@/components/tokens/RevokeTokenDialog";
import { ConnectedApps } from "@/components/oauth/ConnectedApps";
import { AnalyticsSettings } from "@/components/settings/AnalyticsSettings";
import { useTokens } from "@/hooks/useTokens";
import { useRevokeToken } from "@/hooks/useRevokeToken";
import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/hooks/useTenant";
import { canManageTeam } from "@/lib/permissions";
import type { TokenType } from "@/types/token";

const TABS = ["tokens", "connected-apps", "privacy"] as const;
type SettingsTab = (typeof TABS)[number];

function toTab(
  value: string | null,
  available: readonly SettingsTab[],
): SettingsTab {
  return available.includes(value as SettingsTab)
    ? (value as SettingsTab)
    : "tokens";
}

/**
 * Workspace access tokens (admin-managed), the user's own Connected
 * apps (ADR-058), and the user's analytics consent (ADR-051 §5), one tab
 * each. `?tab=connected-apps` is the deep link the connector docs point
 * at; `?tab=privacy` holds the Analytics switch. An unknown value falls
 * back to tokens.
 *
 * Connected apps exists only for workspaces `/v2/mcp` serves
 * (`mcpEnabled` on `GET /v1/dashboard/tenant`; during the MCP beta, the
 * allow-listed ones). Elsewhere, and until that read answers, the tab is
 * not rendered and its deep link falls back to tokens.
 */
export function Settings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: tenant } = useTenant();
  const showConnectedApps = tenant?.mcpEnabled === true;
  const tab = toTab(
    searchParams.get("tab"),
    showConnectedApps ? TABS : TABS.filter((t) => t !== "connected-apps"),
  );
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [defaultTokenType, setDefaultTokenType] =
    useState<TokenType>("backend");
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [tokenToRevoke, setTokenToRevoke] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const { data: tokens, isLoading, error } = useTokens();
  const revokeToken = useRevokeToken();
  const { role } = useAuth();
  const hasManagePermission = canManageTeam(role);

  const handleCreateToken = (type: TokenType) => {
    setDefaultTokenType(type);
    setCreateDialogOpen(true);
  };

  const handleRevokeClick = (tokenId: string) => {
    const token = tokens?.find((t) => t.id === tokenId);
    if (token) {
      setTokenToRevoke({ id: token.id, name: token.name });
      setRevokeDialogOpen(true);
    }
  };

  const handleRevokeConfirm = async () => {
    if (tokenToRevoke) {
      await revokeToken.mutateAsync(tokenToRevoke.id);
    }
  };

  return (
    <DashboardLayout
      title="Settings"
      description={
        showConnectedApps
          ? "Manage access tokens, the apps connected to your account, and your privacy choices"
          : "Manage access tokens and your privacy choices"
      }
      actions={
        tab === "tokens" &&
        hasManagePermission && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button>Generate Token</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleCreateToken("backend")}>
                <Server className="mr-2 h-4 w-4" />
                Backend Token
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleCreateToken("frontend")}>
                <Globe className="mr-2 h-4 w-4" />
                Frontend Token
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleCreateToken("cli")}>
                <Terminal className="mr-2 h-4 w-4" />
                CLI Token
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      }
    >
      <Tabs
        value={tab}
        onValueChange={(value) => {
          setSearchParams({ tab: value }, { replace: true });
        }}
      >
        <TabsList className="mb-4">
          <TabsTrigger value="tokens">Access tokens</TabsTrigger>
          {showConnectedApps && (
            <TabsTrigger value="connected-apps">Connected apps</TabsTrigger>
          )}
          <TabsTrigger value="privacy">Privacy</TabsTrigger>
        </TabsList>
        <TabsContent value="tokens">
          <ErrorBoundary>
            {!hasManagePermission && (
              <Alert className="mb-4">
                <AlertDescription>
                  You need admin or owner permissions to manage access tokens.
                </AlertDescription>
              </Alert>
            )}
            {error && (
              <div className="text-destructive mb-4">
                Error loading tokens: {error.message}
              </div>
            )}
            <TokenList
              tokens={tokens || []}
              isLoading={isLoading}
              onRevoke={hasManagePermission ? handleRevokeClick : undefined}
              onCreateToken={
                hasManagePermission
                  ? () => handleCreateToken("backend")
                  : undefined
              }
            />
          </ErrorBoundary>
        </TabsContent>
        {showConnectedApps && (
          <TabsContent value="connected-apps">
            <ErrorBoundary>
              <ConnectedApps />
            </ErrorBoundary>
          </TabsContent>
        )}
        <TabsContent value="privacy">
          <AnalyticsSettings />
        </TabsContent>
      </Tabs>

      <CreateTokenDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        defaultTokenType={defaultTokenType}
      />

      <RevokeTokenDialog
        open={revokeDialogOpen}
        onOpenChange={setRevokeDialogOpen}
        token={tokenToRevoke}
        onConfirm={handleRevokeConfirm}
      />
    </DashboardLayout>
  );
}
